// Preview-only compatibility and interaction replay. The selected visitor SDK is
// never modified, and its private answers/history remain owned by that SDK.
export function previewStructure(pkg, inspection = {}) {
  const selected = inspection.variant === 'B' && pkg.mag_ab?.b ? { ...pkg, ...pkg.mag_ab.b } : pkg;
  const copy = JSON.parse(JSON.stringify(selected));
  for (const key of ['mag_ab', 'mag_title', 'mag_subtitle', 'mag_welcome', 'mag_launcher_label', 'mag_logo_url', 'mag_logo_fit', 'mag_logo_scale', 'mag_theme_color', 'mag_theme_mode', 'mag_theme_vars', 'mag_font', 'mag_inline_width', 'mag_inline_height', 'mag_input_placeholder', 'mag_success_title', 'mag_success_text', 'mag_summary_heading', 'mag_summary_confirm_label']) delete copy[key];
  const clean = steps => (steps || []).map(step => {
    const next = { ...step };
    delete next.step_prompts; delete next.step_description; delete next.step_answer;
    if (next.step_config) { next.step_config = { ...next.step_config }; delete next.step_config.placeholder; }
    next.step_options = (step.step_options || []).map(option => {
      const result = { ...option }; delete result.icon; delete result.description; return result;
    });
    return next;
  });
  copy.mag_macro_steps = clean(copy.mag_macro_steps);
  copy.mag_routes = Object.fromEntries(Object.entries(copy.mag_routes || {}).map(([key, steps]) => [key, clean(steps)]));
  const stable = value => Array.isArray(value) ? value.map(stable) : value && typeof value === 'object'
    ? Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])])) : value;
  return JSON.stringify(stable({ pkg: copy, route: inspection.route, state: inspection.state, variant: inspection.variant }));
}

// Serialized into the opaque-origin iframe, after the unmodified SDK and sample
// driver. Messages are accepted only from this frame and this build's token.
export function installPreviewBridge({ token, restore = null }) {
  let shadow, stopped = false, replaying = true, actions = [], supported = true, timer;
  let interacted = false, lastStage = null;
  for (const type of ['pointerdown', 'keydown']) document.addEventListener(type, event => { if (event.isTrusted) interacted = true; }, true);
  const send = (kind, value = {}) => {
    if (!stopped) window.parent.postMessage({ type: 'm5m-studio-preview', token, kind, ...value }, '*');
  };
  const input = () => shadow?.querySelector('.m5m-form input:not([type="checkbox"])');
  const choices = () => [...(shadow?.querySelectorAll('.m5m-choice') || [])];
  const location = () => input() ? `input:${input().type}` : choices().length ? `choices:${choices().length}`
    : shadow?.querySelector('.m5m-home') ? 'home' : 'other';
  const snapshot = () => ({ actions, supported, draft: input() ? { value: input().value, type: input().type } : null,
    location: location(), scroll: shadow?.querySelector('.m5m-body')?.scrollTop || 0, pageScroll: window.scrollY || 0 });
  const publish = () => {
    if (replaying) return;
    send('snapshot', { snapshot: snapshot() });
    const stage = shadow?.querySelector('.m5m-success') ? 'success' : shadow?.querySelector('.m5m-summary') ? 'review' : null;
    if (stage !== lastStage) {
      lastStage = stage;
      if (stage) send('position', { position: { stage, interactive: interacted } });
    }
  };
  const remember = action => {
    if (replaying) return;
    if (actions.length >= 60) supported = false;
    else actions.push(action);
    publish();
  };
  const deadline = Date.now() + 8000;
  const waitFor = predicate => new Promise((resolve, reject) => {
    const check = () => {
      if (stopped) { reject(new Error('closed')); return; }
      const value = predicate();
      if (value) { resolve(value); return; }
      if (Date.now() >= deadline) { reject(new Error('replay unavailable')); return; }
      timer = setTimeout(check, 40);
    };
    check();
  });
  const start = async () => {
    // User clicks must not interleave with replay and create unrecorded answers.
    // Programmatic SDK controls still work while the document is inert.
    if (restore) document.body.inert = true;
    try {
      shadow = await waitFor(() => document.querySelector('[id^="m5m-magnet-"]')?.shadowRoot);
      const api = await waitFor(() => window.MachFiveMagnet?.instances?.length && window.MachFiveMagnet);
      if (!shadow.querySelector('.m5m-root.m5m-open')) api.open();
      await waitFor(() => shadow.querySelector('.m5m-root.m5m-open') &&
        shadow.querySelector('.m5m-form,.m5m-choices,.m5m-home,.m5m-bot,.m5m-summary,.m5m-success,.m5m-error'));
      shadow.addEventListener('input', publish);
      shadow.addEventListener('scroll', publish, true);
      window.addEventListener('scroll', publish);
      shadow.addEventListener('submit', event => {
        if (replaying) return;
        const form = event.target.closest('.m5m-form'), field = form?.querySelector('input:not([type="checkbox"])');
        if (!field) { supported = false; publish(); return; }
        const action = { kind: 'input', value: field.value, type: field.type };
        // Invalid submits keep the form mounted. Do not replay them as progress.
        setTimeout(() => { if (!form.isConnected) remember(action); else publish(); }, 0);
      }, true);
      shadow.addEventListener('click', event => {
        if (replaying) return;
        const target = event.target.closest('button,a,input,select');
        if (!target) return;
        if (target.closest('.m5m-form')) {
          if (target.matches('input[type="checkbox"]')) { supported = false; publish(); }
          return;
        }
        if (target.matches('.m5m-choice')) remember({ kind: 'choice', index: choices().indexOf(target) });
        else if (target.matches('[data-m5m-act="chat"],[data-m5m-act="support"]')) remember({ kind: 'route', route: target.dataset.m5mAct });
        else if (target.matches('.m5m-skip')) remember({ kind: 'skip' });
        else { supported = false; publish(); }
        setTimeout(publish, 350);
      }, true);
      // Publish the settled location after SDK transitions, without capturing or
      // reproducing any of the runtime's private state.
      const observer = new MutationObserver(publish);
      observer.observe(shadow, { childList: true, subtree: true });
      window.addEventListener('pagehide', () => { stopped = true; clearTimeout(timer); observer.disconnect(); }, { once: true });
      if (restore) {
        for (const action of restore.actions || []) {
          if (action.kind === 'input') {
            const field = await waitFor(() => input());
            if (field.type !== action.type) throw new Error('input changed');
            field.value = action.value;
            field.dispatchEvent(new Event('input', { bubbles: true }));
            const form = field.closest('form'); form.requestSubmit();
            await waitFor(() => !form.isConnected);
          } else if (action.kind === 'choice') {
            const choice = await waitFor(() => choices()[action.index]); choice.click();
            await waitFor(() => !choice.isConnected);
          } else if (action.kind === 'route') {
            if (!['chat', 'support'].includes(action.route)) throw new Error('unsupported route');
            const button = await waitFor(() => shadow.querySelector(`[data-m5m-act="${action.route}"]`)); button.click();
          } else if (action.kind === 'skip') {
            const button = await waitFor(() => shadow.querySelector('.m5m-skip')); button.click();
            await waitFor(() => !button.isConnected);
          } else throw new Error('unsupported action');
        }
        await waitFor(() => location() === restore.location);
        if (restore.draft) {
          const field = input();
          if (!field || field.type !== restore.draft.type) throw new Error('question changed');
          field.value = restore.draft.value;
          field.dispatchEvent(new Event('input', { bubbles: true }));
        }
        const body = shadow.querySelector('.m5m-body'); if (body) body.scrollTop = restore.scroll || 0;
        if (restore.pageScroll) window.scrollTo(0, restore.pageScroll);
        actions = restore.actions || [];
      }
      replaying = false;
      if (restore) document.body.inert = false;
      publish(); send('ready', { restored: !!restore });
    } catch {
      if (restore) document.body.inert = false;
      if (!stopped) send('failed');
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}

// Finish authoring shows the SDK's actual screen with clearly labeled sample
// answers. Keep visitor controls inert while the sample driver reaches it.
export function installFinishScene({ token, state }) {
  document.body.inert = true;
  let attempts = 0;
  const check = () => {
    const shadow = document.querySelector('[id^="m5m-magnet-"]')?.shadowRoot;
    const ready = shadow?.querySelector(state === 'review' ? '.m5m-summary' : '.m5m-success');
    if (ready || ++attempts >= 250) {
      window.parent.postMessage({ type: 'm5m-studio-preview', token, kind: ready ? 'ready' : 'failed' }, '*');
    } else setTimeout(check, 40);
  };
  check();
}

export function listenToPreviewBridge(frame, getToken, onMessage) {
  const receive = event => {
    if (event.source !== frame.contentWindow || event.data?.type !== 'm5m-studio-preview' || event.data.token !== getToken()) return;
    onMessage(event.data);
  };
  window.addEventListener('message', receive);
  return () => window.removeEventListener('message', receive);
}
